import { Component, OnDestroy, OnInit, ViewChild, HostBinding, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ProductVariantInterface } from 'src/app/model/products/product-variant-interface';
import { VariantVersionInterface } from 'src/app/model/products/variant-version-interface';
import { AttributeService } from 'src/app/services/dataServices/attribute-service/attributeService';
import { AttributeValidationServiceService } from 'src/app/services/dataServices/dataValidation/attribute-validation-service/attribute-validation-service.service';
import { UtilService } from 'src/app/services/util-service/utilService';
import { CommonFeatureService } from 'src/app/services/common-feature-service/common-feature.service';
import { ProductCommonFeatureInterface } from 'src/app/model/commonFeature/product-common-feature-interface';
import { ProductInterface } from 'src/app/model/products/product-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { ProductService } from 'src/app/services/dataServices/product-service/productService';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { CategoryService } from 'src/app/services/dataServices/product-service/categoryService';
import { productCategoryDto } from 'src/app/model/category/productCategoryDto';
import { CustomTextErrorStateMatcher } from "src/environments/validators";
import { NgForm, NgModel  } from '@angular/forms';
import { MinLengthErrorStateMatcher } from 'src/environments/validators';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, filter, catchError, map } from 'rxjs/operators';
import { ProductDetailDisplayFullComponent } from 'src/app/components/marketplace/detail-pages/product-detail-display/product-detail-display-full/product-detail-display-full.component';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { StripeService } from 'src/app/services/stripe-service/stripe.service';
import { SubscriptionData } from 'src/app/model/stripe-models/subscription/subscription-data';
import { CommonFeatureProductCreationComponent } from './common-feature-product-creation/common-feature-product-creation.component';
import { MatSelect } from '@angular/material/select';
import {
    getSessionStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem
} from 'src/app/utils/browser-storage';


@Component({
    selector: 'app-product-screen',
    templateUrl: './product-screen.component.html',
    styleUrls: ['./product-screen.component.css'],
    standalone: false
})
export class ProductScreenComponent implements OnInit, OnDestroy {
    @ViewChild('productForm') form!: NgForm;
    @ViewChild('stepper') stepper: MatStepper;
    @ViewChild('productCategorySelect') productCategorySelect?: MatSelect;
    @ViewChild('productURL') productURLControl?: NgModel;
    @ViewChild(CommonFeatureProductCreationComponent) commonFeatureComponent!: CommonFeatureProductCreationComponent;
    @HostBinding('class.is-create-page') isCreatePage = false;
    public isFirstProductPath = false;

    private readonly STORAGE_KEY = 'product-creation-draft';
    private destroy$ = new Subject<void>();
    private isNavigatingAway = false;

    public product: ProductInterface;
    public categories: productCategoryDto[] = [];
    private featureArray: string[] = [];
    private isFormValid: boolean = false;
    private isDescriptionValid: boolean = true;
    private commonFeatures: ProductCommonFeatureInterface[] = [];
    selectedCategory: any | null = null; // current selected category as a string
    private previousCategory: any | null = null; // to track previous category from saved draft

    productNameErrorStateMatcher = new MinLengthErrorStateMatcher(160);
    variantNameErrorStateMatcher = new CustomTextErrorStateMatcher(160);
    descriptionVariantErrorStateMatcher = new CustomTextErrorStateMatcher(1000);
    versionValid: any;

    private subscriptionId: string;
    private subscriptionName: string;
    private hasMoreSubscriptions: boolean = false;
    private activeOrgId: string;
    public isLoading: boolean = false;
    public formTitle: string = "Produkt anlegen";
    public showSummary: boolean = true;

    constructor(
        private dialog: MatDialog,
        public organisationService: OrganisationService,
        public productService: ProductService,
        public attributeService: AttributeService,
        public router: Router,
        private dialogService: DialogService,
        public attributeValidationService: AttributeValidationServiceService,
        private commonFeatureService: CommonFeatureService,
        private userService: UserService,
        private titleService: Title,
        private categoryService: CategoryService,
        public utilService: UtilService,
        private cdr: ChangeDetectorRef,
        private stripeService: StripeService,

    ) {
        //Reroutes to marketplace if the user is not a member of the organisation
        if (
            !this.userService.currentMemberships.find(
                (orga) =>
                    orga.organisationId ===
                    this.organisationService.activeItem.organisationId
            )
        ) {
            this.router.navigateByUrl('/marktplatz');
        }
        document.documentElement.style.setProperty('--scrollStatus', 'auto');
        this.titleService.setTitle(
            'Produkt anlegen: ' +
            this.organisationService.activeItem.organisationName +
            ' - BIMSWARM'
        );
    }

    ngOnInit(): void {
        window.addEventListener('beforeunload', this.unloadHandler);
        this.activeOrgId = String(this.organisationService.activeItem.organisationId);
        this.isCreatePage = this.router.url.includes('/products/create');
        this.isFirstProductPath = this.router.url.includes('/firstProduct');
        this.categories = this.categoryService.categories$.getValue();
        this.utilService.setDirty(true);

        // Attempt to load saved draft
        if (this.isCreatePage) {
            this.loadDraft();
        }

        // If no draft was loaded, initialize a new product
        if (!this.product) {
            this.initializeNewProduct();
        }
        this.getSubscriptionData(this.activeOrgId, '', '5');

        this.previousCategory = this.product?.category ?? null;
        this.selectedCategory = this.previousCategory;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: BeforeUnloadEvent) {
        if (this.hasPendingChanges()) {
            event.preventDefault();
            event.returnValue = true; // Required by Chrome
        }
    }

    private unloadHandler = (event: BeforeUnloadEvent) => {
        if (this.hasPendingChanges()) {
            event.preventDefault();
            event.returnValue = ''; // required for Chrome
        }
    };

    hasPendingChanges(): boolean {
        return this.utilService.isDirty();
    }

    discardChanges(): void {
        this.clearDraft();
    }

    /**
     * Initialises a new empty product
     */
    private initializeNewProduct(): void {
        if (this.organisationService && this.organisationService.activeItem) {
            this.product = {
                company: this.organisationService.activeItem.organisationId,
                category: '',
                description: '',
                fileId: null,
                slogan: '',
                url: '',
                features: [],
                galleryFileIds: [],
                documentFileIds: [],
                relatedProducts: [],
                productVariants: [
                    {
                        name: '',
                        description: '',
                        variantVersions: [
                            {
                                name: '',
                                description: '',
                                attributeValidationValues: [],
                                ptgVersions: [],
                                ptgVersionIds: [],
                                keywords: [],
                            }
                        ]
                    }
                ]
            };
            this.featureArray = [];
            this.commonFeatures = [];
            this.selectedCategory = null;
        }
    }

    ngOnDestroy(): void {
        document.documentElement.style.setProperty('--scrollStatus', 'hidden');
        window.removeEventListener('beforeunload', this.unloadHandler);
        if (!this.isLoading && !this.isNavigatingAway) {
            this.clearDraft();
        }

        this.destroy$.next();
        this.destroy$.complete();
    }

    createProduct(shouldPublish: boolean = false): void {
        this.isLoading = true;
        // first variant
        let productVariant: ProductVariantInterface =
            this.product.productVariants[0];
        // first version
        let productVariantVersion: VariantVersionInterface =
            productVariant.variantVersions[0];
        // validate
        //Check for string lengths
        if (
            !UtilService.areStringsValid(
                this.product.name,
                this.product.description,
            )
        ) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen sie sicher, dass der Name oder die Beschreibung nicht zu lang sind.'
            );
            this.isLoading = false;
            return;
        }
        if (this.product.category === null || this.product.category === undefined) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Kategorie für das Produkt.'
            );
            this.isLoading = false;
            return;
        }

        if (!UtilService.areStringsValid(productVariant.name, null)) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen sie sicher, dass der Name der Variante nicht zu lang ist.'
            );
            this.isLoading = false;
            return;
        }
        if (!UtilService.areStringsValid(productVariantVersion.name, null)) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen sie sicher, dass der Name der Version nicht zu lang ist.'
            );
            this.isLoading = false;
            return;
        }
        if (this.product.fileId == null) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Logo für das Produkt.'
            );
            this.isLoading = false;
            return;
        }

        if (this.product.description == null) {
            this.product.description = '';
        }
        if (productVariant.description == null) {
            productVariant.description = '';
        }
        if (productVariantVersion.description == null) {
            productVariantVersion.description = '';
        }
        productVariantVersion.attributeValidationValues =
            this.attributeValidationService.getAttributeValidationValues();
        this.product.features = this.featureArray;
        const matchedCategory = this.categories.find(c => c.name === this.product.category);
        this.product.category = matchedCategory ? matchedCategory.value : 0;
        this.applyDescriptionOrderToVersionName();
        this.isLoading = true;
        this.productService.createProduct(this.product).then(
            async (p: ProductInterface) => {
                // this.productService.loadProductsForOrganisation(
                //     this.organisationService.activeOrganisation
                // );

                this.commonFeatureService
                    .addFeaturesForProduct(
                        p.id,
                        this.commonFeatures.map((elem) => elem.entry.id)
                    )
                    .subscribe((success) => { });

                this.clearDraft();
                // case: provider has one subscription
                if (shouldPublish && this.hasMoreSubscriptions === false) {
                    this.licenseAndPublishProduct(p);
                } else if (shouldPublish && this.hasMoreSubscriptions === true) {
                    // case: provider has more than one subscription
                    this.isLoading = false;
                    this.chooseProductLicense(p.id.toString());
                } else {
                    // case: provider has no subscription
                    this.productService.loadProductsForOrganisation(
                        this.organisationService.activeOrganisation
                    );
                    this.isLoading = false;
                    this.router.navigateByUrl(
                        '/organisations/' +
                        this.organisationService.activeItem.organisationId +
                        '/products/' +
                        p.id
                    );
                    this.dialogService.openDialog(
                        'Produkt gespeichert',
                        'Ihr Produkt ' + p.name + ' wurde erfolgreich in Ihrem Kundenkonto gespeichert. Falls Sie noch kein Abonnement haben, können Sie es jetzt buchen.'
                    );
                }
                window.removeEventListener('beforeunload', this.unloadHandler);
                this.utilService.setDirty(false);
            },
            (e: ErrorEvent) => {
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        );
    }

    /**
     * Checks wether ptgs or common features are selected and displays a dialog when changing the category
     * @param newCategory - selected category in dropdown menu
     * @returns 
     */
    onCategoryChange(newCategory: any) {
        const prev = this.previousCategory ?? this.selectedCategory ?? '';
        const variantVersion = this.product?.productVariants?.[0]?.variantVersions?.[0];
        const hasPtgs =
            Array.isArray(variantVersion?.ptgVersions) &&
            variantVersion.ptgVersions.length > 0;
        const hasCommonFeatures =
            Array.isArray(this.commonFeatures) &&
            this.commonFeatures.length > 0;

        if (!hasPtgs && !hasCommonFeatures) {
            this.applyCategoryChange(newCategory);
            return;
        }

        this.dialogService.openConfirmDialog(
            'Produktkategorie ändern',
            'Nein',
            'Ja',
            'Änderungen der Produktkategorie wirken sich auf die Auswahl der Produkttypen und übergeordneten Auswahlkriterien aus. Möchten Sie die Produktkategorie wirklich ändern?'
        ).pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result) {
                    this.applyCategoryChange(newCategory);
                } else {
                    this.product.category = prev;
                    this.selectedCategory = prev;

                    if (this.productCategorySelect) {
                        try {
                            (this.productCategorySelect as any).value = prev;
                        } catch (e) {
                            console.error('Fehler beim Zurücksetzen der Kategorie:', e);
                        }
                        this.cdr.detectChanges();
                    }
                }
            });
    }

    /**
     * Changes produt category and resets ptg selections. Common feature selection is handled in component.
     * @param category - selected category in dropdown menu
     */
    private applyCategoryChange(category: any): void {
        this.selectedCategory = category;
        this.product.category = category;
        this.previousCategory = category;

        const variantVersion =
            this.product?.productVariants?.[0]?.variantVersions?.[0];

        if (variantVersion) {
            variantVersion.ptgVersions = [];
            variantVersion.ptgVersionIds = [];
        }

        this.saveDraft();
    }

    public showCommonFeatureSelectionDialog() {
        this.dialogService
            .openCommonFeatureEditDialog([...this.commonFeatures])
            .subscribe((success) => {
                if (success) {
                    this.commonFeatures = success;
                    this.saveDraft();
                }
            });
    }

    public onValidityChanged(valid: boolean): void {
        this.isFormValid = valid;
        this.versionValid = valid;
    }

    public onDescriptionValidityChange(descriptionValid: boolean): void {
        this.isDescriptionValid = descriptionValid;
    }

    isSaveDisabled(): boolean {
        return (
            !this.isFormValid ||
            this.form.invalid ||
            !this.isDescriptionValid ||
            this.product.fileId === null ||
            this.product.category === '' ||
            this.product.category === null

        )
    }

    onCommonFeaturesChanged(features: ProductCommonFeatureInterface[]): void {
        this.commonFeatures = features;
        this.saveDraft();
    }

    isDetailsStepComplete(): boolean {
        const hasBasicData  = !!this.product?.name &&
            this.product.name.trim().length > 0 &&
            this.product.category !== null &&
            this.product.category !== '' &&
            this.product.url !== null &&
            this.product.url !== '';

        const isProductNameValid = !!this.product?.name && this.product.name.length <= 160 && this.product.name.length > 1;
        const isUrlValid = this.productURLControl?.valid ?? false;
        const isDescriptionValid = this.isDescriptionValid;

        return hasBasicData && isProductNameValid && isUrlValid && isDescriptionValid;
    }

    isGalleryStepComplete(): boolean {
        const hasFile = !!this.product?.fileId;
        return hasFile;
    }

    isVariantVersionStepComplete(): boolean {
        const variantName = this.product?.productVariants?.[0]?.name;
        const hasVariantName = !!variantName && variantName.trim().length > 0 && variantName.length <= 160;
        const isVariantDescriptionValid = this.product?.productVariants?.[0]?.description.length <= 1000;
    
        return hasVariantName &&
            isVariantDescriptionValid &&  
            !!this.versionValid;
    }

    public isCommonFeatureStepValid(): boolean {
        if (!this.commonFeatureComponent) return false;
        return this.commonFeatureComponent.hasAllRequiredSelections();
    }

    onVersionValidityChanged(valid: boolean): void {
        this.versionValid = valid;
    }

    openProductDetailsPageDialog(title: string): Observable<any> {
        let productVariant: ProductVariantInterface = this.product.productVariants[0];
        let productVariantVersion: VariantVersionInterface = this.product.productVariants[0].variantVersions[0];
        if (this.product.fileId == null) {
            this.product.fileId = '';
        }
        if (this.product.name == null) {
            this.product.name = '';
        }
        if (this.product.description == null) {
            this.product.description = '';
        }
        if (productVariant.name == null) {
            productVariant.name = '';
        }
        if (productVariantVersion.name == null) {
            productVariantVersion.name = '';
        }
        if (productVariant.description == null) {
            productVariant.description = '';
        }
        if (productVariantVersion.description == null) {
            productVariantVersion.description = '';
        }
        productVariantVersion.attributeValidationValues = this.attributeValidationService.getPreviewAttributeValidationValues();
        this.product.features = this.featureArray;
        const reference = this.dialog.open(ProductDetailDisplayFullComponent, {
            width: '95vw',
            maxWidth: '95vw',
            height: '90vh',
            data: {
                title: 'Produktübersicht',
                product: this.product,
                preview: true,
                commonFeatures: this.commonFeatures
            }
        });
        return reference.afterClosed();
    }

    // Methodes to publish the product

    private licenseAndPublishProduct(p: ProductInterface): void {
        this.licenseProduct(p.id.toString()).subscribe(success => {
            if (success) {
                this.productService.reload(p.id).then(() => {
                    this.handlePostCreationFlow(p);
                });
            } else {
                this.isLoading = false;
                this.router.navigateByUrl(
                    '/organisations/' +
                    this.organisationService.activeItem.organisationId +
                    '/products/' +
                    p.id
                );
            }
        });
    }

    private licenseProduct(productId: string): Observable<boolean> {
        if (!productId || !this.subscriptionId) {
            this.dialogService.openDialog(
                'Zuordnung fehlgeschlagen',
                'Fehler bei der Zuordnung Ihres Produkts zum Abonnement.'
            );
            return of(false);
        }

        return this.stripeService.addProductsToListing(
            this.activeOrgId,
            this.subscriptionId,
            productId
        ).pipe(
            map(response => {
                if (Array.isArray(response) && response.includes('Your listing package is full.')) {
                    this.dialogService.openDialog(
                        'Abonnement voll',
                        'Ihr Abonnement ist bereits vollständig belegt.'
                    );
                    return false;
                }

                if (typeof response === 'object' && response?.status && [403, 404, 500].includes(response.status)) {
                    this.dialogService.openDialog(
                        'Zuordnung fehlgeschlagen',
                        'Fehler bei der Zuordnung Ihres Produkts zum Abonnement.'
                    );
                    return false;
                }

                return true;
            }),
            catchError(() => {
                this.dialogService.openDialog(
                    'Unerwarteter Fehler',
                    'Bitte versuchen Sie es später erneut.'
                );
                return of(false);
            })
        );
    }

    private handlePostCreationFlow(p: ProductInterface): void {
        this.isLoading = false;

        if (['Professional', 'Enterprise'].includes(this.subscriptionName)) {
            this.askAndHandlePrioritization(p);
        } else {
            this.dialogService.openDialog(
                'Produkt erstellt',
                `Das neue Produkt ${p.name} wurde erfolgreich erstellt und veröffentlicht.`
            );
            this.router.navigateByUrl('marktplatz/products/' + p.id);
        }
    }

    private askAndHandlePrioritization(p: ProductInterface): void {
        this.dialogService.openConfirmDialog(
            'Produkt erstellt und veröffentlicht',
            'Nein',
            'Ja',
            'Möchten Sie das Produkt entsprechend Ihrem Tarif priorisieren?'
        ).subscribe((result) => {
            if (result) {
                this.isLoading = true;
                this.stripeService.prioritizeProduct(
                    this.activeOrgId,
                    this.subscriptionId,
                    p.id.toString(),
                    true
                ).subscribe((prioritizeResponse) => {
                    this.isLoading = false;
                    if (prioritizeResponse?.status !== 200) {
                        this.dialogService.openDialog(
                            'Priorisierung fehlgeschlagen',
                            'Die Priorisierung ist fehlgeschlagen. Bitte versuchen Sie es später erneut.'
                        );
                    } else {
                        this.dialogService.openDialog('Produkt priorisiert', '');
                    }
                    this.router.navigateByUrl('marktplatz/products/' + p.id);
                });
            } else {
                this.router.navigateByUrl('marktplatz/products/' + p.id);
            }
        });
    }

    getSubscriptionData(orgId: string, paginationDirectionBill: string, limit: string) {
        const onFirstProductPage = this.router.url.includes('/firstProduct');
        this.stripeService.getSubscriptionData(orgId, paginationDirectionBill, limit)
            .subscribe({
                next: (data: any) => {
                    if (data && Array.isArray(data.subscriptions) && data.subscriptions.length > 0) {
                        const relevantSub = this.getRelevantSubscription(data.subscriptions);
                        if (relevantSub) {
                            this.subscriptionId = relevantSub.id;
                            this.subscriptionName = relevantSub.name;
                        } else {
                            if (onFirstProductPage) {
                                this.dialogService.openDialog(
                                    'Kein gültiges Abonnements gefunden',
                                    'Es wurde kein gültiges Abonnement für Ihre Organisation gefunden.');
                            } else {
                                this.subscriptionId = null;
                                this.subscriptionName = null;
                            }
                        }
                    } else {
                        if (onFirstProductPage) {
                            this.dialogService.openDialog(
                                'Keine aktiven Abonnements gefunden',
                                'Es wurde kein aktives Abonnement für Ihre Organisation gefunden.');
                        } else {
                            this.subscriptionId = null;
                            this.subscriptionName = null;
                        }
                    }
                },
                error: () => {
                    if (onFirstProductPage) {
                        this.dialogService.openDialog(
                            'Fehler beim Laden der Abonnementdaten',
                            'Beim Abrufen Ihrer Abonnementdaten ist ein Fehler aufgetreten.'
                        );
                    }
                }
            });
    }

    getRelevantSubscription(subscriptions: SubscriptionData[]): SubscriptionData | null {
        if (!subscriptions || subscriptions.length === 0) {
            return null;
        }

        if (subscriptions.length === 1) {
            return subscriptions[0];
        }

        const validSubscriptions = subscriptions.filter(sub =>
            !sub.name?.toLowerCase().includes('prioritätslizenz')
        );
        if (validSubscriptions.length > 1) this.hasMoreSubscriptions = true;

        return validSubscriptions.length > 0 ? validSubscriptions[0] : null;
    }

    chooseProductLicense(productId: string): void {
        this.dialogService.openchangeProductLicenseDialog(
            'Das Produkt wurde gespeichert. Wählen Sie den Tarif, mit dem Sie dieses Produkt veröffentlichen wollen',
            'Abbrechen',
            'Veröffentlichen',
            productId).subscribe({
                next: () => {
                    this.router.navigateByUrl(
                        '/organisations/' +
                        this.organisationService.activeItem.organisationId +
                        '/products/' +
                        productId
                    );
                }
            });
    }

    // Session Storage Draft Methods

    /**
     * Saves the current state in sessionStorage
     */
    private saveDraft(): void {
        if (!this.isCreatePage) return;

        try {
            const draft = {
                product: this.product,
                featureArray: this.featureArray,
                commonFeatures: this.commonFeatures,
                selectedCategory: this.selectedCategory,
                currentStep: this.stepper?.selectedIndex || 0,
                timestamp: new Date().toISOString(),
                organisationId: this.organisationService.activeItem.organisationId
            };

            setSessionStorageItem(this.STORAGE_KEY, JSON.stringify(draft));
        } catch (error) {
            console.error('Fehler beim Speichern des Entwurfs:', error);
        }
    }

    /**
     * Loads the saved draft from sessionStorage
     */
    private loadDraft(): void {
        try {
            const saved = getSessionStorageItem(this.STORAGE_KEY);
            if (!saved) return;

            const draft = JSON.parse(saved);

            // Check if the draft belongs to the current organization
            if (draft.organisationId !== this.organisationService.activeItem.organisationId) {
                this.clearDraft();
                return;
            }

            if (draft.timestamp) {
                const savedTime = new Date(draft.timestamp).getTime();
                const now = Date.now();
                const hoursDiff = (now - savedTime) / (1000 * 60 * 60);

                if (hoursDiff > 24) {
                    this.clearDraft();
                    return;
                }
            }

            this.initializeNewProduct();

            this.dialogService.openConfirmDialog(
                'Entwurf gefunden',
                'Verwerfen',
                'Fortsetzen',
                'Ein Entwurf wurde gefunden. Möchten Sie mit diesem Entwurf fortfahren?'
            ).subscribe(result => {
                if (result) {
                    this.restoreDraft(draft);
                } else {
                    this.clearDraft();
                }
            });
        } catch (error) {
            console.error('Fehler beim Laden des Entwurfs:', error);
            this.clearDraft();
        }
    }

    /**
     * Restores the draft
     */
    private restoreDraft(draft: any): void {
        this.product = draft.product;
        const variantVersion = this.product?.productVariants?.[0]?.variantVersions?.[0];
        // Clear attribute validation values and ptg versions to avoid inconsistent data display
        if (variantVersion) {
            variantVersion.attributeValidationValues = [];
            variantVersion.ptgVersions = [];
            variantVersion.ptgVersionIds = [];
        }
        
        this.featureArray = draft.featureArray || [];
        this.commonFeatures = draft.commonFeatures || [];
        this.selectedCategory = draft.selectedCategory;

        // Wait until the view is initialized
        setTimeout(() => {
            if (this.form) {
                this.form.form.markAllAsTouched();
                this.form.form.updateValueAndValidity();
            }

            // Set validity status based on draft data
            this.checkAndUpdateValidityStates();

            // Jump to the saved step
            if (this.stepper && draft.currentStep !== undefined) {
                this.stepper.selectedIndex = draft.currentStep;
                setTimeout(() => {
                    if (this.form) {
                        this.form.form.updateValueAndValidity();
                    }
                    this.checkAndUpdateValidityStates();
                    this.cdr.detectChanges();
                }, 100);
            }
        }, 200);
    }

    /**
     * Checks and updates all validity states after draft restoration
     */
    private checkAndUpdateValidityStates(): void {
        // Check description validity
        if (this.product.description) {
            this.isDescriptionValid = true;
        }

        // Check form validity
        if (this.form) {
            this.isFormValid = this.form.valid;
        }
    }

    /**
     * Deletes the saved draft
     */
    private clearDraft(): void {
        try {
            removeSessionStorageItem(this.STORAGE_KEY);
        } catch (error) {
            console.error('Fehler beim Löschen des Entwurfs:', error);
        }
    }

    /**
     * Called when the user clicks "Next"
     * Template: (click)="onStepNext()"
     */
    public onStepNext(): void {
        this.saveDraft();
        this.cdr.detectChanges();
        this.stepper.next();
    }

    /**
     * Called when the user clicks "Back"
     * Template: (click)="onStepPrevious()"
     */
    public onStepPrevious(): void {
        this.saveDraft();
        this.cdr.detectChanges();
        this.stepper.previous();
    }

    onStepChange(event: any): void {
        if (event.selectedIndex === 4) {
            this.reloadSummary();
            this.cdr.detectChanges();
        }
        this.saveDraft();
    }

    reloadSummary(): void {
        this.showSummary = false;
        this.product.productVariants[0].variantVersions[0].attributeValidationValues = this.attributeValidationService.getPreviewAttributeValidationValues();
        setTimeout(() => {
            this.showSummary = true;
        }, 0);
    }

    /**
     * Cancel with confirmation
     */
    cancelCreateProduct(): void {
                this.isNavigatingAway = true;
                
                if (this.productService.activeItem) {
                    this.router.navigate([
                        '/organisations/' +
                        this.organisationService.activeItem.organisationId +
                        '/products/' +
                        this.productService.activeItem.id
                    ]);
                } else {
                    this.router.navigate([
                        '/organisations/' +
                        this.organisationService.activeItem.organisationId +
                        '/products'
                    ]);
                }
                // clearDraft() is handled by auth guard!
    }

    private applyDescriptionOrderToVersionName(): void {
        const variantVersion = this.product.productVariants[0].variantVersions[0];
        const orderParts: string[] = [];

        variantVersion.ptgVersions.forEach(v => {
            const match = v.description.match(/<<>>(\d+),(\d+)$/);
            if (match) {
                const id = match[1];
                const order = match[2];
                orderParts.push(`${id},${order}`);
                v.description = '';
            }
        });

        if (orderParts.length > 0) {
            variantVersion.name = variantVersion.name.replace(/\s*<<>>[\d, -]+$/, '');
            variantVersion.name = `${variantVersion.name}<<>>${orderParts.join('-')}`;
        }
    }
}
